/**
 * 配列フィールドエディタ — array<{...}> 形式の content を編集する共通 UI。
 *
 * 各セクションの array 系フィールド（products, features, testimonials, ...）に対応。
 * 子フィールドのスキーマは ARRAY_FIELD_SCHEMAS で type ごとに定義。
 */
import { ChevronDown, ChevronUp, GripVertical, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { CtaHrefField } from './CtaHrefField';
import { ImageFieldEditor } from './ImageFieldEditor';
import { LucideIconPicker } from './LucideIconPicker';
import { VideoSourceFieldEditor } from './VideoSourceFieldEditor';

import { Input, Label, Textarea } from '@/components/ui/input';

export interface SubFieldSchema {
  key: string;
  label: string;
  type:
    | 'text'
    | 'textarea'
    | 'image'
    | 'array_string'
    | 'link_list'
    | 'lucide_icon'
    | 'video_source'
    | 'spec_list'
    | 'cta_url';
  max?: number;
  placeholder?: string;
}

/**
 * UI フィールド型 → 子フィールドスキーマ
 */
export const ARRAY_FIELD_SCHEMAS: Record<string, SubFieldSchema[]> = {
  product_list: [
    { key: 'name', label: '名前', type: 'text', max: 30 },
    { key: 'category', label: 'カテゴリ (Workstation / Laptop など)', type: 'text', max: 24 },
    { key: 'tagline', label: 'タグライン', type: 'textarea', max: 50 },
    { key: 'price', label: '価格', type: 'text', max: 24 },
    { key: 'image', label: '画像URL', type: 'image' },
    { key: 'specs', label: 'スペック (ラベル / 値)', type: 'spec_list' },
    { key: 'ctaLabel', label: 'CTA ラベル', type: 'text', max: 24 },
    { key: 'ctaHref', label: 'CTA リンク', type: 'cta_url' },
  ],
  // 製品スペック行 (label / value のペア)
  spec_list: [
    { key: 'label', label: 'ラベル', type: 'text', max: 24 },
    { key: 'value', label: '値', type: 'text', max: 60 },
  ],
  // feature_spotlight 用: サブ見出し + 本文ブロック
  spotlight_blocks: [
    { key: 'heading', label: 'サブ見出し (任意)', type: 'text', max: 60 },
    { key: 'body', label: '本文', type: 'textarea', max: 600 },
  ],
  feature_list: [
    { key: 'title', label: 'タイトル', type: 'text', max: 50 },
    { key: 'description', label: '説明', type: 'textarea', max: 160 },
    { key: 'icon', label: 'アイコン (lucide)', type: 'lucide_icon' },
    { key: 'image', label: '画像URL', type: 'image' },
  ],
  qa_list: [
    { key: 'question', label: '質問', type: 'text', max: 120 },
    { key: 'answer', label: '回答', type: 'textarea', max: 400 },
    { key: 'category', label: 'カテゴリ', type: 'text', max: 30 },
  ],
  testimonial_list: [
    { key: 'quote', label: '引用', type: 'textarea', max: 280 },
    { key: 'name', label: '氏名', type: 'text', max: 40 },
    { key: 'role', label: '役職', type: 'text', max: 60 },
    { key: 'company', label: '会社', type: 'text', max: 40 },
    { key: 'avatar', label: 'アバターURL', type: 'image' },
    { key: 'rating', label: '★ 評価 (1-5)', type: 'text', max: 4 },
  ],
  plan_list: [
    { key: 'name', label: 'プラン名', type: 'text', max: 30 },
    { key: 'price', label: '価格', type: 'text', max: 20 },
    { key: 'period', label: '期間', type: 'text', max: 20 },
    { key: 'description', label: '説明', type: 'textarea', max: 120 },
    { key: 'highlighted', label: 'おすすめ表示 (true/false)', type: 'text', max: 5 },
    {
      key: 'badge',
      label: 'バッジ文言 (おすすめ時のみ、省略で "Most popular")',
      type: 'text',
      max: 20,
    },
    { key: 'ctaLabel', label: 'CTA ラベル', type: 'text', max: 24 },
    { key: 'ctaHref', label: 'CTA リンク', type: 'cta_url' },
    { key: 'features', label: '機能一覧', type: 'array_string' },
  ],
  video_list: [
    { key: 'title', label: 'タイトル', type: 'text', max: 60 },
    { key: 'description', label: '説明', type: 'textarea', max: 160 },
    { key: 'poster', label: 'ポスター画像URL', type: 'image' },
    { key: 'videoUrl', label: '動画', type: 'video_source' },
    { key: 'duration', label: '長さ', type: 'text', max: 12 },
  ],
  team_list: [
    { key: 'name', label: '氏名', type: 'text', max: 40 },
    { key: 'role', label: '役職', type: 'text', max: 60 },
    { key: 'bio', label: 'プロフィール', type: 'textarea', max: 200 },
    { key: 'avatar', label: 'アバターURL', type: 'image' },
  ],
  stat_list: [
    { key: 'value', label: '数値', type: 'text', max: 16 },
    { key: 'label', label: 'ラベル', type: 'text', max: 60 },
    { key: 'context', label: '補足', type: 'text', max: 100 },
  ],
  step_list: [
    { key: 'title', label: 'タイトル', type: 'text', max: 50 },
    { key: 'description', label: '説明', type: 'textarea', max: 200 },
    { key: 'image', label: '画像URL', type: 'image' },
  ],
  comparison_rows: [
    { key: 'feature', label: '項目', type: 'text', max: 80 },
    { key: 'us', label: '自社', type: 'text', max: 60 },
    { key: 'them', label: '他社', type: 'text', max: 60 },
  ],
  logo_list: [
    { key: 'name', label: '名前', type: 'text', max: 40 },
    { key: 'image', label: '画像URL', type: 'image' },
  ],
  // フッター列（タイトル + リンクリスト）
  footer_columns: [
    { key: 'title', label: 'タイトル', type: 'text', max: 30 },
    { key: 'links', label: 'リンク', type: 'link_list' },
  ],
  // フッターのリンク配列（ネスト用）
  link_list: [
    { key: 'label', label: 'ラベル', type: 'text', max: 30 },
    { key: 'href', label: 'URL', type: 'text' },
  ],
};

interface Props {
  /** field type (product_list, feature_list, ...) */
  fieldType: string;
  items: Array<Record<string, unknown>>;
  onChange: (next: Array<Record<string, unknown>>) => void;
  itemLabel?: (item: Record<string, unknown>, idx: number) => string;
  /** 画像フィールドの Storage パス（landing/{landingId}） */
  landingId?: string;
}

export function ArrayFieldEditor({ fieldType, items, onChange, itemLabel, landingId }: Props) {
  const subFields = ARRAY_FIELD_SCHEMAS[fieldType];
  const [expandedIdx, setExpandedIdx] = useState<number | null>(0);

  if (!subFields) {
    // 未対応の type は JSON テキストにフォールバック
    return <JsonFallback items={items} onChange={onChange} />;
  }

  const handleItemChange = (idx: number, key: string, value: unknown) => {
    const next = items.slice();
    next[idx] = { ...next[idx], [key]: value };
    onChange(next);
  };

  const handleAdd = () => {
    const empty: Record<string, unknown> = {};
    for (const f of subFields) {
      // リスト型は空配列で作る（空文字だと公開ページ側で .map できず画面全体が落ちる）
      empty[f.key] =
        f.type === 'array_string' || f.type === 'link_list' || f.type === 'spec_list' ? [] : '';
    }
    onChange([...items, empty]);
    setExpandedIdx(items.length);
  };

  const handleRemove = (idx: number) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
    if (expandedIdx === idx) setExpandedIdx(null);
  };

  const handleMove = (idx: number, dir: -1 | 1) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const next = items.slice();
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {items.map((item, idx) => {
        const expanded = expandedIdx === idx;
        const label = itemLabel
          ? itemLabel(item, idx)
          : String(item[subFields[0].key] ?? `Item ${idx + 1}`);
        return (
          <div
            key={idx}
            className="border border-[var(--border)] rounded-md bg-[var(--bg-elevated)]"
          >
            <div className="flex items-center gap-1 p-2">
              <GripVertical className="size-3.5 text-[var(--text-muted)] cursor-grab" />
              <button
                type="button"
                onClick={() => setExpandedIdx(expanded ? null : idx)}
                className="flex-1 text-left text-xs font-medium truncate"
              >
                {idx + 1}. {label || '(無題)'}
              </button>
              <button
                type="button"
                onClick={() => handleMove(idx, -1)}
                disabled={idx === 0}
                className="p-1 text-[var(--text-muted)] disabled:opacity-30"
                title="上へ"
              >
                <ChevronUp className="size-3" />
              </button>
              <button
                type="button"
                onClick={() => handleMove(idx, 1)}
                disabled={idx === items.length - 1}
                className="p-1 text-[var(--text-muted)] disabled:opacity-30"
                title="下へ"
              >
                <ChevronDown className="size-3" />
              </button>
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="p-1 text-[var(--text-muted)] hover:text-[var(--accent-red)]"
                title="削除"
              >
                <X className="size-3" />
              </button>
            </div>
            {expanded && (
              <div className="border-t border-[var(--border)] p-2 space-y-2 bg-[var(--bg-elevated)]">
                {subFields.map((f) => (
                  <SubFieldRow
                    key={f.key}
                    schema={f}
                    value={item[f.key]}
                    onChange={(v) => handleItemChange(idx, f.key, v)}
                    item={item}
                    onItemChange={(next) => {
                      const arr = items.slice();
                      arr[idx] = next;
                      onChange(arr);
                    }}
                    landingId={landingId}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={handleAdd}
        className="w-full text-xs text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-md py-2 hover:bg-[var(--bg-hover)]"
      >
        <Plus className="size-3 inline mr-1" /> 追加
      </button>
    </div>
  );
}

function SubFieldRow({
  schema,
  value,
  onChange,
  item,
  onItemChange,
  landingId,
}: {
  schema: SubFieldSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  item: Record<string, unknown>;
  onItemChange: (next: Record<string, unknown>) => void;
  landingId?: string;
}) {
  if (schema.type === 'array_string') {
    return (
      <ArrayStringEditor
        label={schema.label}
        value={Array.isArray(value) ? (value as string[]) : []}
        onChange={onChange}
      />
    );
  }
  if (schema.type === 'image') {
    return (
      <ImageFieldEditor
        label={schema.label}
        value={typeof value === 'string' ? value : null}
        onChange={(v) => onChange(v ?? '')}
        storagePath="marketing/lp/imgs"
      />
    );
  }
  if (schema.type === 'cta_url') {
    return (
      <CtaHrefField
        label={schema.label}
        value={typeof value === 'string' ? value : ''}
        onChange={(v) => onChange(v)}
      />
    );
  }
  if (schema.type === 'video_source') {
    return <VideoSourceFieldEditor label={schema.label} item={item} onItemChange={onItemChange} />;
  }
  if (schema.type === 'lucide_icon') {
    return (
      <LucideIconPicker
        label={schema.label}
        value={typeof value === 'string' ? value : ''}
        onChange={(v) => onChange(v)}
      />
    );
  }
  if (schema.type === 'link_list') {
    return (
      <div>
        <Label className="text-[11px]">{schema.label}</Label>
        <ArrayFieldEditor
          fieldType="link_list"
          items={Array.isArray(value) ? (value as Array<Record<string, unknown>>) : []}
          onChange={(next) => onChange(next)}
          itemLabel={(item) => (typeof item.label === 'string' ? (item.label as string) : '')}
          landingId={landingId}
        />
      </div>
    );
  }
  if (schema.type === 'spec_list') {
    return (
      <div>
        <Label className="text-[11px]">{schema.label}</Label>
        <ArrayFieldEditor
          fieldType="spec_list"
          items={Array.isArray(value) ? (value as Array<Record<string, unknown>>) : []}
          onChange={(next) => onChange(next)}
          itemLabel={(item) => {
            const l = typeof item.label === 'string' ? item.label : '';
            const v = typeof item.value === 'string' ? item.value : '';
            return l || v ? `${l}${l && v ? ': ' : ''}${v}` : '';
          }}
          landingId={landingId}
        />
      </div>
    );
  }
  return (
    <BlurField
      label={schema.label}
      value={typeof value === 'string' ? value : ''}
      multiline={schema.type === 'textarea'}
      placeholder={schema.placeholder}
      max={schema.max}
      onSave={onChange}
    />
  );
}

function BlurField({
  label,
  value,
  multiline,
  placeholder,
  max,
  onSave,
}: {
  label: string;
  value: string;
  multiline?: boolean;
  placeholder?: string;
  max?: number;
  onSave: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  const composing = useRef(false);
  return (
    <div>
      <div className="flex justify-between items-baseline">
        <Label className="text-[11px]">{label}</Label>
        {max && (
          <span className="text-[10px] text-[var(--text-muted)]">
            {local.length}/{max}
          </span>
        )}
      </div>
      {multiline ? (
        <Textarea
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => {
            if (composing.current) return;
            if (local !== value) onSave(local);
          }}
          onCompositionStart={() => (composing.current = true)}
          onCompositionEnd={() => (composing.current = false)}
          placeholder={placeholder}
          rows={2}
        />
      ) : (
        <Input
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => {
            if (composing.current) return;
            if (local !== value) onSave(local);
          }}
          onCompositionStart={() => (composing.current = true)}
          onCompositionEnd={() => (composing.current = false)}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

function ArrayStringEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const handleItemChange = (idx: number, v: string) => {
    const next = value.slice();
    next[idx] = v;
    onChange(next);
  };
  return (
    <div>
      <Label className="text-[11px]">{label}</Label>
      <div className="space-y-1">
        {value.map((item, i) => (
          <div key={i} className="flex gap-1">
            <BlurField label="" value={item} max={120} onSave={(v) => handleItemChange(i, v)} />
            <button
              type="button"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="p-1 text-[var(--text-muted)]"
            >
              <X className="size-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...value, ''])}
          className="text-[11px] text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded px-2 py-1 hover:bg-[var(--bg-hover)]"
        >
          + 追加
        </button>
      </div>
    </div>
  );
}

function JsonFallback({
  items,
  onChange,
}: {
  items: Array<Record<string, unknown>>;
  onChange: (next: Array<Record<string, unknown>>) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(items, null, 2));
  useEffect(() => setText(JSON.stringify(items, null, 2)), [items]);
  return (
    <Textarea
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        try {
          const parsed = JSON.parse(text);
          if (Array.isArray(parsed)) onChange(parsed);
        } catch {
          /* ignore */
        }
      }}
      rows={8}
      className="font-mono text-[11px]"
    />
  );
}
